import { useState, useRef, useEffect } from "react";
import { useList, useCreate } from "@refinedev/core";
import { LiaTimesSolid } from "react-icons/lia";

interface CreatableComboboxProps {
    value?: string[];
    onChange?: (val: string[]) => void;
}

export const CreatableCombobox = ({ value = [], onChange }: CreatableComboboxProps) => {
    const [inputValue, setInputValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { data, isLoading } = useList({
        resource: "article_labels",
        pagination: { mode: "off" },
        meta: {
            fields: ['id']
        }
    });

    const { mutate: createLabel, isLoading: isCreating } = useCreate();

    const existingLabels = data?.data.map((item: any) => item.id) || [];

    // Format input: lowercase and replace spaces with underscores
    const formatLabel = (str: string) => str.toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');

    const formattedInput = formatLabel(inputValue);

    const filteredOptions = existingLabels.filter((label: string) =>
        label.includes(formattedInput) && !value.includes(label)
    );

    const canCreate = formattedInput.length > 0 && !existingLabels.includes(formattedInput) && !value.includes(formattedInput);

    const handleAdd = (label: string) => {
        if (!value.includes(label)) {
            onChange?.([...value, label]);
        }
        setInputValue("");
        setIsOpen(false);
    };

    const handleCreate = () => {
        if (!canCreate) return;
        const newLabel = formattedInput;
        // Optimistically add to the current value
        handleAdd(newLabel);
        // Mutate to backend
        createLabel({
            resource: "article_labels",
            values: { id: newLabel },
            successNotification: false,
        });
    };

    const handleRemove = (labelToRemove: string) => {
        onChange?.(value.filter(label => label !== labelToRemove));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div
                className={`input input-bordered flex flex-wrap items-center gap-2 h-auto min-h-[3rem] py-1 ${isOpen ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setIsOpen(true)}
            >
                {value.map(label => (
                    <span key={label} className="badge badge-primary gap-1 p-3">
                        {label}
                        <LiaTimesSolid
                            className="cursor-pointer hover:text-base-100"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(label);
                            }}
                        />
                    </span>
                ))}
                <input
                    type="text"
                    className="flex-1 min-w-[120px] bg-transparent outline-none h-8"
                    placeholder={value.length === 0 ? "Escribe para buscar o crear..." : ""}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (canCreate) {
                                handleCreate();
                            } else if (filteredOptions.length > 0 && formattedInput) {
                                handleAdd(filteredOptions[0]);
                            }
                        }
                    }}
                />
            </div>

            {isOpen && (inputValue.length > 0 || filteredOptions.length > 0 || isLoading) && (
                <ul className="absolute z-50 w-full mt-1 bg-base-100 rounded-box shadow-lg max-h-60 overflow-auto menu menu-sm p-2 border border-base-300">
                    {isLoading && <li><span className="loading loading-spinner loading-sm m-2"></span></li>}

                    {!isLoading && filteredOptions.map((label: string) => (
                        <li key={label}>
                            <a onClick={() => handleAdd(label)}>{label}</a>
                        </li>
                    ))}

                    {!isLoading && canCreate && (
                        <li>
                            <a
                                className="text-primary font-bold"
                                onClick={handleCreate}
                            >
                                Crear "{formattedInput}" {isCreating && <span className="loading loading-spinner opacity-50 w-4 h-4 ml-2"></span>}
                            </a>
                        </li>
                    )}

                    {!isLoading && filteredOptions.length === 0 && !canCreate && inputValue.length > 0 && (
                        <li><span className="text-base-content/50 pointer-events-none p-2">No hay más opciones</span></li>
                    )}
                </ul>
            )}
        </div>
    );
};
